import { z } from "zod";

import { LOCALES } from "@/i18n/types";

export const navigationItemSchema = z.object({
  href: z.string(),
  label: z.string(),
});

export const actionLinkSchema = z.object({
  download: z.boolean().optional(),
  external: z.boolean().optional(),
  href: z.string(),
  label: z.string(),
});

export const footerGroupSchema = z.object({
  links: z.array(navigationItemSchema),
  title: z.string(),
});

export const themeTokensSchema = z.object({
  accent: z.string(),
  background: z.string(),
  foreground: z.string(),
  heroGlow: z.string(),
  primary: z.string(),
  radius: z.string(),
  secondary: z.string(),
  surface: z.string(),
  surfaceMuted: z.string(),
  surfaceStrong: z.string(),
});

export const pageIntroSchema = z.object({
  description: z.string(),
  eyebrow: z.string(),
  title: z.string(),
});

export const statItemSchema = z.object({
  label: z.string(),
  value: z.string(),
});

export const profilePanelSchema = z.object({
  focusAreas: z.array(z.string()).min(3),
  recentRoles: z.array(z.string()).min(3),
  stats: z.array(statItemSchema).min(3),
  summary: z.string(),
  title: z.string(),
});

export const heroSectionSchema = z.object({
  actions: z.array(actionLinkSchema).min(2),
  description: z.string(),
  profilePanel: profilePanelSchema,
  title: z.string(),
});

export const homePortraitSchema = z.object({
  alt: z.string(),
  locationLabel: z.string(),
  monogram: z.string(),
  note: z.string(),
  src: z.string().optional(),
});

export const homeHeroSchema = z.object({
  actions: z.array(actionLinkSchema).min(2),
  description: z.string(),
  name: z.string(),
  portrait: homePortraitSchema,
  title: z.string(),
});

export const homeProofItemSchema = z.object({
  lines: z.array(z.string()).min(2).max(3),
  title: z.string(),
});

export const homeProofSectionSchema = z.object({
  items: z.array(homeProofItemSchema).length(3),
});

export const homeTimelineItemSchema = z.object({
  description: z.string(),
  title: z.string(),
});

export const homeTimelineSectionSchema = z.object({
  items: z.array(homeTimelineItemSchema).length(3),
  title: z.string(),
});

export const homeClosingSectionSchema = z.object({
  action: actionLinkSchema,
  description: z.string(),
  title: z.string(),
});

export const featureItemSchema = z.object({
  description: z.string(),
  title: z.string(),
});

export const featureSectionSchema = z.object({
  description: z.string(),
  eyebrow: z.string(),
  items: z.array(featureItemSchema).min(1),
  title: z.string(),
});

export const aboutSectionSchema = z.object({
  body: z.array(z.string()).min(2),
  eyebrow: z.string(),
  highlights: z.array(z.string()).min(3),
  title: z.string(),
});

export const ctaSectionSchema = z.object({
  description: z.string(),
  href: z.string(),
  label: z.string(),
  title: z.string(),
});

export const faqItemSchema = z.object({
  answer: z.string(),
  question: z.string(),
});

export const faqSectionSchema = z.object({
  description: z.string(),
  eyebrow: z.string(),
  items: z.array(faqItemSchema).min(1),
  title: z.string(),
});

export const contactDetailsSchema = z.object({
  availability: z.string(),
  email: z.email(),
  responseWindow: z.string(),
});

export const serviceItemSchema = z.object({
  ctaHref: z.string().optional(),
  ctaLabel: z.string().optional(),
  description: z.string(),
  icon: z.string().optional(),
  title: z.string(),
});

export const servicesListSectionSchema = z.object({
  description: z.string(),
  eyebrow: z.string(),
  items: z.array(serviceItemSchema).min(1),
  title: z.string(),
});

export const testimonialItemSchema = z.object({
  author: z.string(),
  quote: z.string(),
  role: z.string(),
});

export const testimonialsSectionSchema = z.object({
  description: z.string().optional(),
  eyebrow: z.string(),
  items: z.array(testimonialItemSchema).min(1),
  title: z.string(),
});

export const galleryItemSchema = z.object({
  alt: z.string(),
  caption: z.string(),
  src: z.string(),
});

export const gallerySectionSchema = z.object({
  description: z.string().optional(),
  eyebrow: z.string(),
  items: z.array(galleryItemSchema).min(1),
  title: z.string(),
});

export const contactSectionSchema = z.object({
  description: z.string().optional(),
  eyebrow: z.string(),
  title: z.string(),
});

export const legalSectionSchema = z.object({
  sections: z.array(
    z.object({
      body: z.array(z.string()).min(1),
      title: z.string(),
    }),
  ),
  updatedAt: z.string(),
});

export const snapshotSectionSchema = z.object({
  items: z.array(z.string()).min(4),
  title: z.string(),
});

export const previewSectionSchema = z.object({
  description: z.string(),
  eyebrow: z.string(),
  title: z.string(),
});

export const experienceEntrySchema = z.object({
  company: z.string(),
  contributions: z.array(z.string()).min(2),
  demonstrates: z.string(),
  icon: z.string().optional(),
  impact: z.string(),
  location: z.string(),
  period: z.string(),
  role: z.string(),
  summary: z.string(),
  team: z.string().optional(),
  technologies: z.array(z.string()).min(3),
});

export const projectLinkSchema = actionLinkSchema.extend({
  note: z.string().optional(),
});

export const projectEntrySchema = z.object({
  links: z.array(projectLinkSchema).optional(),
  name: z.string(),
  role: z.string(),
  summary: z.string(),
  shortDescription: z.string(),
  status: z.string(),
  technologies: z.array(z.string()).min(3),
});

export const educationItemSchema = z.object({
  degree: z.string(),
  gpa: z.string().optional(),
  graduation: z.string(),
  institution: z.string(),
});

export const skillGroupSchema = z.object({
  items: z.array(z.string()).min(1),
  title: z.string(),
});

export const educationSectionSchema = z.object({
  description: z.string(),
  eyebrow: z.string(),
  item: educationItemSchema,
  skillGroups: z.array(skillGroupSchema).min(2),
  title: z.string(),
});

export const skillsPageSectionSchema = z.object({
  description: z.string(),
  groups: z.array(skillGroupSchema).min(2),
  title: z.string(),
});

export const contactMethodSchema = z.object({
  external: z.boolean().optional(),
  href: z.string(),
  label: z.string(),
  showValue: z.boolean().optional(),
  value: z.string(),
});

export const contactHubSchema = z.object({
  description: z.string(),
  methods: z.array(contactMethodSchema).min(2),
  note: z.string().optional(),
  resume: actionLinkSchema.optional(),
  title: z.string(),
});

export const localeContentSchema = z.object({
  contact: contactHubSchema,
  contactDetails: contactDetailsSchema.optional(),
  experience: z.object({
    items: z.array(experienceEntrySchema).min(3),
  }),
  footerGroups: z.array(footerGroupSchema).min(2),
  headerAction: actionLinkSchema.optional(),
  home: z.object({
    closing: homeClosingSectionSchema,
    hero: homeHeroSchema,
    proof: homeProofSectionSchema,
    timeline: homeTimelineSectionSchema,
  }),
  legal: legalSectionSchema.optional(),
  navigation: z.array(navigationItemSchema).min(4),
  pages: z.object({
    contact: pageIntroSchema,
    experience: pageIntroSchema,
    home: pageIntroSchema,
    projects: pageIntroSchema,
    skills: pageIntroSchema,
  }),
  projects: z.object({
    items: z.array(projectEntrySchema).min(3),
  }),
  skillsPage: skillsPageSectionSchema,
  sections: z
    .object({
      contact: contactSectionSchema.optional(),
      gallery: gallerySectionSchema.optional(),
      servicesList: servicesListSectionSchema.optional(),
      testimonials: testimonialsSectionSchema.optional(),
    })
    .optional(),
  site: z.object({
    description: z.string(),
    name: z.string(),
    tagline: z.string(),
  }),
});

export const sharedSiteSchema = z.object({
  defaultOgImage: z.string().default("/og/default.png"),
  locales: z.array(z.enum(LOCALES as unknown as [string, ...string[]])).min(1),
  organization: z
    .object({
      legalName: z.string(),
      logo: z.string().optional(),
      url: z.string(),
    })
    .optional(),
  theme: themeTokensSchema,
  twitterHandle: z.string().optional(),
});

export const siteConfigSchema = z.object({
  locales: z.record(
    z.enum(LOCALES as unknown as [string, ...string[]]),
    localeContentSchema,
  ),
  shared: sharedSiteSchema,
});

export type NavigationItem = z.infer<typeof navigationItemSchema>;
export type ActionLink = z.infer<typeof actionLinkSchema>;
export type FooterGroup = z.infer<typeof footerGroupSchema>;
export type ThemeTokens = z.infer<typeof themeTokensSchema>;
export type PageIntro = z.infer<typeof pageIntroSchema>;
export type StatItem = z.infer<typeof statItemSchema>;
export type ProfilePanel = z.infer<typeof profilePanelSchema>;
export type HeroSection = z.infer<typeof heroSectionSchema>;
export type HomePortrait = z.infer<typeof homePortraitSchema>;
export type HomeHero = z.infer<typeof homeHeroSchema>;
export type HomeProofItem = z.infer<typeof homeProofItemSchema>;
export type HomeProofSection = z.infer<typeof homeProofSectionSchema>;
export type HomeTimelineItem = z.infer<typeof homeTimelineItemSchema>;
export type HomeTimelineSection = z.infer<typeof homeTimelineSectionSchema>;
export type HomeClosingSection = z.infer<typeof homeClosingSectionSchema>;
export type FeatureItem = z.infer<typeof featureItemSchema>;
export type FeatureSection = z.infer<typeof featureSectionSchema>;
export type AboutSection = z.infer<typeof aboutSectionSchema>;
export type CtaSection = z.infer<typeof ctaSectionSchema>;
export type FaqItem = z.infer<typeof faqItemSchema>;
export type FaqSection = z.infer<typeof faqSectionSchema>;
export type ContactDetails = z.infer<typeof contactDetailsSchema>;
export type ServiceItem = z.infer<typeof serviceItemSchema>;
export type ServicesListSection = z.infer<typeof servicesListSectionSchema>;
export type TestimonialItem = z.infer<typeof testimonialItemSchema>;
export type TestimonialsSection = z.infer<typeof testimonialsSectionSchema>;
export type GalleryItem = z.infer<typeof galleryItemSchema>;
export type GallerySection = z.infer<typeof gallerySectionSchema>;
export type ContactSection = z.infer<typeof contactSectionSchema>;
export type LegalSection = z.infer<typeof legalSectionSchema>;
export type SnapshotSection = z.infer<typeof snapshotSectionSchema>;
export type PreviewSection = z.infer<typeof previewSectionSchema>;
export type ExperienceEntry = z.infer<typeof experienceEntrySchema>;
export type ProjectLink = z.infer<typeof projectLinkSchema>;
export type ProjectEntry = z.infer<typeof projectEntrySchema>;
export type EducationItem = z.infer<typeof educationItemSchema>;
export type SkillGroup = z.infer<typeof skillGroupSchema>;
export type EducationSection = z.infer<typeof educationSectionSchema>;
export type SkillsPageSection = z.infer<typeof skillsPageSectionSchema>;
export type ContactMethod = z.infer<typeof contactMethodSchema>;
export type ContactHub = z.infer<typeof contactHubSchema>;
export type LocaleContent = z.infer<typeof localeContentSchema>;
export type SharedSiteConfig = z.infer<typeof sharedSiteSchema>;
export type SiteConfig = z.infer<typeof siteConfigSchema>;
